import type { ConexaoDoKommo } from "@base/contracts";
import { Button, cn } from "@base/ui";
import {
  AlertTriangle,
  ArrowUpRight,
  CalendarClock,
  Database,
  History,
  KeyRound,
  Layers,
  ListFilter,
  RefreshCw,
  ShieldCheck,
  Workflow,
  type LucideIcon,
} from "lucide-react";
import { useState } from "react";
import { mensagemDoErro } from "../../../shared/http/mensagem-de-erro";
import { navegar } from "../../../shared/navegacao/navegacao";
import { formatar } from "../../dashboard";
import { useDesconectarKommo, useSincronizarKommo } from "../application/conexoes.contexto";
import { FormularioDeConexao } from "./painel-do-kommo";

const HORA_MS = 60 * 60 * 1000;

/** "agora há pouco", "há 12 min", "há 3 h", "há 2 dias". */
function haQuanto(iso: string, agora = Date.now()): string {
  const minutos = Math.max(0, Math.round((agora - Date.parse(iso)) / 60_000));
  if (minutos < 1) return "agora há pouco";
  if (minutos < 60) return `há ${minutos} min`;
  const horas = Math.round(minutos / 60);
  if (horas < 24) return `há ${horas} h`;
  const dias = Math.round(horas / 24);
  return `há ${dias} ${dias === 1 ? "dia" : "dias"}`;
}

const SELO: Readonly<Record<ConexaoDoKommo["situacao"], { rotulo: string; ponto: string; fundo: string }>> = {
  conectada: { rotulo: "Conectada", ponto: "bg-success", fundo: "bg-success/10 text-success" },
  sincronizando: { rotulo: "Sincronizando", ponto: "bg-warning", fundo: "bg-warning/15 text-warning-foreground" },
  "com-erro": { rotulo: "Com erro", ponto: "bg-destructive", fundo: "bg-destructive/10 text-destructive" },
  "nao-conectada": { rotulo: "Não conectada", ponto: "bg-muted-foreground", fundo: "bg-muted text-muted-foreground" },
};

/**
 * Aba "Conta" da tela do Kommo: a conta conectada num cartao de destaque, com
 * a mesma altura do painel verde dos numeros ao lado.
 */
export function AbaDaConta({ clienteId, conexao }: { readonly clienteId: string; readonly conexao: ConexaoDoKommo }) {
  const sincronizar = useSincronizarKommo(clienteId);
  const desconectar = useDesconectarKommo(clienteId);
  const [trocandoToken, setTrocandoToken] = useState(false);
  const [confirmando, setConfirmando] = useState(false);
  const sincronizando = conexao.situacao === "sincronizando" || sincronizar.isPending;
  const etapas = conexao.pipelines.reduce((total, pipeline) => total + pipeline.etapas.length, 0);
  const erro = sincronizar.error ?? desconectar.error;
  const selo = SELO[conexao.situacao];
  const ultima = conexao.ultimaSincronizacaoEm;

  return (
    <div className="flex flex-col gap-bloco">
      <div className="grid gap-bloco lg:grid-cols-[minmax(0,1fr)_20rem]">
        <section className="relative flex flex-col overflow-hidden rounded-3xl bg-card shadow-sm ring-1 ring-border/70">
          {/* Brilho suave no canto: da profundidade sem competir com o conteudo. */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute -top-24 -right-24 size-72 rounded-full bg-ice/25 blur-3xl"
          />

          <div className="relative flex flex-1 flex-col gap-8 p-6 md:p-8">
            <div className="flex items-start justify-between gap-4">
              <span className="grid size-14 place-items-center rounded-2xl bg-secondary text-ice shadow-md ring-4 ring-ice/25">
                <Database className="size-6" aria-hidden="true" />
              </span>
              <span className={cn("inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold", selo.fundo)}>
                <span className="relative flex size-2">
                  {conexao.situacao !== "com-erro" ? (
                    <span className={cn("absolute inline-flex size-full animate-ping rounded-full opacity-60", selo.ponto)} />
                  ) : null}
                  <span className={cn("relative inline-flex size-2 rounded-full", selo.ponto)} />
                </span>
                {selo.rotulo}
              </span>
            </div>

            <div className="flex flex-col gap-1.5">
              <p className="text-xs font-semibold tracking-[0.12em] text-muted-foreground uppercase">Conta Kommo</p>
              <h2 className="text-2xl font-medium tracking-tight md:text-3xl">{conexao.nomeDaConta}</h2>
              <a
                href={`https://${conexao.subdominio}.kommo.com`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex w-fit items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-primary"
              >
                {conexao.subdominio}.kommo.com
                <ArrowUpRight className="size-3.5" aria-hidden="true" />
              </a>
            </div>

            <dl className="grid gap-px overflow-hidden rounded-2xl bg-border/70 ring-1 ring-border/70 sm:grid-cols-3">
              <Info
                icone={History}
                rotulo="Última sincronização"
                valor={ultima ? haQuanto(ultima) : "Ainda não"}
                detalhe={ultima ? formatar.momento(ultima) : "Escolha as etapas para começar"}
              />
              <Info
                icone={CalendarClock}
                rotulo="Próxima automática"
                valor={conexao.mapeamento ? "Em até 1 hora" : "Pausada"}
                detalhe={
                  conexao.mapeamento && ultima
                    ? `Por volta de ${formatar.momento(new Date(Date.parse(ultima) + HORA_MS).toISOString()).split(" às ")[1]}`
                    : "Depois do mapeamento do funil"
                }
              />
              <Info icone={ShieldCheck} rotulo="Token" valor="Cifrado" detalhe="Nunca volta para a tela" />
            </dl>

            {conexao.ultimoErro ? (
              <p role="alert" className="flex items-start gap-2 rounded-2xl bg-destructive/10 px-4 py-3 text-sm text-destructive">
                <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
                {conexao.ultimoErro}
              </p>
            ) : null}
            {erro ? (
              <p role="alert" className="text-sm text-destructive">
                {mensagemDoErro(erro)}
              </p>
            ) : null}

            {trocandoToken ? (
              <div className="rounded-2xl bg-muted/50 p-5">
                <FormularioDeConexao
                  className="max-w-xl"
                  clienteId={clienteId}
                  subdominioAtual={conexao.subdominio}
                  aoConcluir={() => setTrocandoToken(false)}
                  aoCancelar={() => setTrocandoToken(false)}
                />
              </div>
            ) : null}
          </div>

          <div className="relative flex flex-wrap items-center gap-2 border-t bg-muted/30 px-6 py-4 md:px-8">
            <Button
              disabled={!conexao.mapeamento || sincronizando}
              onClick={() => sincronizar.mutate()}
              title={conexao.mapeamento ? undefined : "Escolha as etapas do funil antes"}
            >
              <RefreshCw className={cn(sincronizando && "animate-spin")} aria-hidden="true" />
              {sincronizando ? "Sincronizando…" : "Sincronizar agora"}
            </Button>
            <Button variant="outline" onClick={() => setTrocandoToken((atual) => !atual)}>
              <KeyRound aria-hidden="true" />
              Trocar token
            </Button>
            <span className="ml-auto">
              {confirmando ? (
                <span className="flex flex-wrap items-center gap-2 text-sm">
                  Desconectar? O Dashboard volta aos números zerados.
                  <Button
                    size="sm"
                    variant="destructive"
                    disabled={desconectar.isPending}
                    onClick={() =>
                      desconectar.mutate(undefined, { onSuccess: () => navegar("/configuracoes/conexoes") })
                    }
                  >
                    Desconectar
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setConfirmando(false)}>
                    Cancelar
                  </Button>
                </span>
              ) : (
                <button
                  type="button"
                  className="text-sm text-muted-foreground underline-offset-4 transition-colors hover:text-destructive hover:underline"
                  onClick={() => setConfirmando(true)}
                >
                  Desconectar
                </button>
              )}
            </span>
          </div>
        </section>

        <aside className="flex flex-col rounded-3xl bg-secondary p-6 text-secondary-foreground md:p-8">
          <p className="text-xs font-semibold tracking-[0.12em] uppercase opacity-75">Nesta conta</p>
          <ul className="mt-4 flex flex-1 flex-col divide-y divide-white/10">
            <Numero icone={Workflow} valor={conexao.pipelines.length} rotulo={conexao.pipelines.length === 1 ? "Funil" : "Funis"} />
            <Numero icone={Layers} valor={etapas} rotulo="Etapas" />
            <Numero icone={ListFilter} valor={conexao.camposPersonalizados.length} rotulo="Campos de lead" />
          </ul>
          <p className="mt-4 text-sm leading-relaxed opacity-75">
            {conexao.mapeamento
              ? "Leads, etapas e vendas chegam sozinhos a cada hora."
              : "Escolha as etapas do funil para começar a sincronizar."}
          </p>
        </aside>
      </div>

      {!conexao.mapeamento ? (
        <p className="flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-warning/10 px-5 py-3 text-sm">
          Falta escolher as etapas do funil para o Dashboard usar os dados do Kommo.
          <Button size="sm" variant="outline" onClick={() => navegar("/configuracoes/conexoes/kommo?aba=funil")}>
            Escolher etapas
          </Button>
        </p>
      ) : null}
    </div>
  );
}

function Info({
  icone: Icone,
  rotulo,
  valor,
  detalhe,
}: {
  readonly icone: LucideIcon;
  readonly rotulo: string;
  readonly valor: string;
  readonly detalhe: string;
}) {
  return (
    <div className="flex flex-col gap-1 bg-card px-4 py-4">
      <dt className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <Icone className="size-3.5" aria-hidden="true" />
        {rotulo}
      </dt>
      <dd className="flex flex-col">
        <span className="text-base font-semibold">{valor}</span>
        <span className="text-xs text-muted-foreground">{detalhe}</span>
      </dd>
    </div>
  );
}

function Numero({ icone: Icone, valor, rotulo }: { readonly icone: LucideIcon; readonly valor: number; readonly rotulo: string }) {
  return (
    <li className="flex flex-1 items-center justify-between gap-3 py-4">
      <span className="flex items-center gap-3 text-sm opacity-85">
        <span className="grid size-9 place-items-center rounded-xl bg-white/10 text-ice">
          <Icone className="size-4" aria-hidden="true" />
        </span>
        {rotulo}
      </span>
      <span className="text-3xl font-medium tracking-tight tabular-nums">{formatar.quantidade(valor)}</span>
    </li>
  );
}
